import { Link, useParams, useSearchParams } from 'react-router-dom';

import { JsonLd } from '@/components/JsonLd';
import { PageMeta, SITE_URL } from '@/components/PageMeta';
import { ROUTES } from '@/config/routes';
import { getPublishedArticlesApi } from '@/lib/api/blogApi';
import { getClubApi } from '@/lib/api/clubsApi';
import { getOrganizationApi } from '@/lib/api/organizationsApi';
import { getFileUrl } from '@/lib/file-url';
import { queryKeys } from '@/lib/query/queryKeys';
import { formatDateAbbreviatedFr } from '@shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, BookOpen, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

const PAGE_SIZE = 9;

// Le temps de lecture n'est plus estimé ici. Depuis la projection publique
// allégée (31/07/2026), l'API ne renvoie plus le HTML des articles dans les
// listings et calcule elle-même readTimeMinutes : le repli local n'a plus
// d'objet, et il n'aurait plus de contenu sur lequel travailler.

/** Fenêtre glissante : jamais plus de 5 numéros, contrairement au blog global. */
function pageWindow(current: number, total: number): number[] {
  const start = Math.max(1, Math.min(current - 2, total - 4));
  const end = Math.min(total, start + 4);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

type Scope = 'club' | 'organization';

/**
 * Sous-ensemble commun à `Club` et `Organization`, seuls champs utilisés ici.
 * Indispensable : `getClubApi` et `getOrganizationApi` renvoient deux types
 * disjoints (`Club` a registrationNumber/clubPlan/isActive), et TanStack Query
 * ne peut pas inférer TQueryFnData depuis une union de promesses.
 */
type EntitySummary = { name: string; isProfilePublic: boolean };

export default function EntityBlog({ scope }: { scope: Scope }) {
  const { id } = useParams<{ id: string }>();
  const entityId = Number(id);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get('page') ?? '1'));

  const isClub = scope === 'club';

  const { data: entity, isError } = useQuery<EntitySummary>({
    queryKey: isClub ? queryKeys.clubs.detail(entityId) : queryKeys.organizations.detail(entityId),
    queryFn: async (): Promise<EntitySummary> =>
      isClub ? await getClubApi(entityId) : await getOrganizationApi(entityId),
    enabled: !isNaN(entityId),
  });

  const isPublic = entity?.isProfilePublic === true;

  const queryParams = {
    page,
    limit: PAGE_SIZE,
    ...(isClub ? { clubId: entityId } : { organizationId: entityId }),
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.blog.articles(queryParams),
    queryFn: () => getPublishedArticlesApi(queryParams),
    enabled: !isNaN(entityId) && isPublic,
  });

  const articles = data?.data ?? [];
  const totalPages = data?.meta?.totalPages ?? 0;
  const profilePath = isClub ? `/clubs/${entityId}` : `/organisateurs/${entityId}`;

  // Entité inexistante ou privée : la fiche elle-même est inaccessible, le blog
  // doit l'être aussi (règle B3, appliquée en plus côté serveur dans findAll).
  if (isNaN(entityId) || isError || (entity && !isPublic)) {
    return (
      <div className='min-h-[60vh] flex items-center justify-center bg-[#F8FAFC]'>
        <PageMeta title='Blog introuvable' description="Ce blog n'est pas accessible." robots='noindex, nofollow' />
        <div className='text-center'>
          <h1 className='text-xl font-bold text-slate-900 mb-2'>Blog introuvable</h1>
          <Link
            to={isClub ? ROUTES.CLUBS : ROUTES.SEARCH_ORGANIZATIONS}
            className='text-sm font-semibold text-slate-600 hover:text-slate-900'
          >
            Retour à l&apos;annuaire
          </Link>
        </div>
      </div>
    );
  }

  const entityName = entity?.name ?? '';
  const accentText = isClub ? 'text-emerald-600 hover:text-emerald-700' : 'text-purple-600 hover:text-purple-700';
  const accentBadge = isClub ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700';

  return (
    <div className='bg-[#F8FAFC] min-h-screen'>
      <PageMeta
        title={`Blog — ${entityName}`}
        description={`Actualités et articles publiés par ${entityName} sur BoulesConnect.`}
        // Page « thin » tant qu'aucun article n'est publié : on évite de créer
        // des centaines d'URL vides dans l'index.
        robots={articles.length === 0 ? 'noindex, follow' : undefined}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Accueil', item: SITE_URL },
            {
              '@type': 'ListItem',
              position: 2,
              name: isClub ? 'Clubs' : 'Organisateurs',
              item: `${SITE_URL}${isClub ? '/clubs' : '/organisateurs'}`,
            },
            { '@type': 'ListItem', position: 3, name: entityName, item: `${SITE_URL}${profilePath}` },
            { '@type': 'ListItem', position: 4, name: 'Blog', item: `${SITE_URL}${profilePath}/blog` },
          ],
        }}
      />

      <section className='px-6 pt-32 pb-10'>
        <div className='max-w-6xl mx-auto'>
          <Link to={profilePath} className={`inline-flex items-center gap-2 text-sm font-semibold mb-4 ${accentText}`}>
            <ArrowLeft className='w-4 h-4' /> Retour à la fiche de {entityName}
          </Link>
          <h1 className='text-3xl md:text-5xl font-extrabold text-slate-900'>Blog — {entityName}</h1>
        </div>
      </section>

      {isLoading && (
        <div className='flex justify-center py-20'>
          <div className='h-8 w-8 animate-spin rounded-full border-2 border-slate-400 border-t-transparent' />
        </div>
      )}

      {!isLoading && articles.length === 0 && (
        <section className='px-6 pb-20'>
          <div className='max-w-6xl mx-auto text-center py-20 bg-white rounded-3xl border border-slate-200'>
            <BookOpen className='w-10 h-10 text-slate-200 mx-auto mb-3' />
            <p className='text-slate-400 text-sm'>Aucun article publié pour le moment.</p>
          </div>
        </section>
      )}

      {!isLoading && articles.length > 0 && (
        <section className='px-6 pb-12'>
          <div className='max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {articles.map(article => {
              const cover = article.files?.find(f => f.usage === 'ARTICLE_COVER') ?? null;
              const readTime = article.readTimeMinutes;
              return (
                <Link
                  key={article.id}
                  to={ROUTES.ARTICLE.replace(':slug', article.slug)}
                  className='bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all group'
                >
                  {cover ? (
                    <div className='h-48 overflow-hidden'>
                      <img
                        src={getFileUrl(cover)}
                        alt={article.title}
                        className='h-full w-full object-cover transition-transform group-hover:scale-105'
                      />
                    </div>
                  ) : (
                    <div className='h-48 bg-slate-100 flex items-center justify-center'>
                      <BookOpen className='w-8 h-8 text-slate-300' />
                    </div>
                  )}
                  <div className='p-6'>
                    {article.category && (
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-3 ${accentBadge}`}>
                        {article.category.name}
                      </span>
                    )}
                    <h2 className='text-lg font-bold text-slate-900 mb-2 leading-snug line-clamp-2'>{article.title}</h2>
                    {article.excerpt && <p className='text-sm text-slate-500 mb-4 line-clamp-2'>{article.excerpt}</p>}
                    <div className='flex items-center gap-3 text-xs text-slate-400'>
                      <span>{formatDateAbbreviatedFr(article.publishedAt ?? article.createdAt)}</span>
                      {readTime && (
                        <span className='flex items-center gap-1'>
                          <Clock className='w-3 h-3' /> {readTime} min
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <section className='px-6 pb-20'>
          <div className='max-w-6xl mx-auto flex justify-center items-center gap-1'>
            <button
              onClick={() => setSearchParams({ page: String(page - 1) }, { replace: true })}
              disabled={page <= 1}
              className='w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center disabled:opacity-30'
            >
              <ChevronLeft className='w-4 h-4 text-slate-400' />
            </button>
            {pageWindow(page, totalPages).map(p => (
              <button
                key={p}
                onClick={() => setSearchParams({ page: String(p) }, { replace: true })}
                className={`w-10 h-10 rounded-xl text-sm font-bold ${
                  p === page ? 'bg-slate-900 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setSearchParams({ page: String(page + 1) }, { replace: true })}
              disabled={page >= totalPages}
              className='w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center disabled:opacity-30'
            >
              <ChevronRight className='w-4 h-4 text-slate-400' />
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
